import React from "react";
import mascotNoData from "../../Assets/icons/okkali_mascot_nodata.png";

const ReportTable = ({
  title = "Veri Listesi",
  count = 0,
  countText = "kayıt bulundu",
  columns = [],
  data = [],
  renderCell = (item, column, index) => item[column.key] || "-",
  onRowClick,
  emptyMessage = "Veri bulunamadı",
  // Başlık bloğu varsayılan kapalı — rapor adı zaten sayfa tepesinde
  showHeader = false,
  className = "",
  isLoading = false,
  skeletonRows = 5,
  paginate = false,
  defaultPageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  resetKey,
  compact = false, // Compact mod: kart kaldır, pagination alta al, yükseklik sınırı kaldır
  maxHeightClass = "max-h-96", // Dikey kaydırma yüksekliği (compact değilken) — sayfa boşluğunu kullanmak için override edilebilir
}) => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [resetKey]);

  // Loading skeleton state
  if (isLoading) {
    return (
      <div
        className={`bg-white border-y border-gray-200 overflow-hidden -mx-3 sm:-mx-4 ${className}`}
      >
        {showHeader && (
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">{title}</h3>
                <p className="text-sm text-gray-600">
                  {count} {countText}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Skeleton Table */}
        <div className="p-6">
          {/* Table Header Skeleton */}
          <div className="mb-4">
            <div className="h-12 bg-gray-200 rounded-xl animate-pulse"></div>
          </div>

          {/* Skeleton Rows */}
          {[...Array(skeletonRows)].map((_, index) => (
            <div key={index} className="mb-3">
              <div className="flex gap-3">
                {columns.map((_, colIndex) => (
                  <div
                    key={colIndex}
                    className="h-8 bg-gray-200 rounded-lg animate-pulse flex-1"
                    style={{
                      animationDelay: `${index * 0.1 + colIndex * 0.1}s`,
                    }}
                  ></div>
                ))}
              </div>
            </div>
          ))}

          {/* Progress Indicator */}
          <div className="mt-6 text-center">
            <p className="text-gray-600 mb-3 font-medium">
              Veriler yükleniyor...
            </p>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-gray-50/60 border border-gray-200 rounded-2xl p-8 sm:p-12 text-center select-none">
        <div className="flex flex-col items-center gap-3 sm:gap-5">
          <img
            src={mascotNoData}
            alt="Veri bulunamadı"
            className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 object-contain drop-shadow-lg"
            draggable={false}
          />
          <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-700">
            {emptyMessage}
          </h3>
        </div>
      </div>
    );
  }

  const totalCount = data.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const pagedData = paginate
    ? data.slice(startIndex, startIndex + pageSize)
    : data;

  const getAlignClass = (align = "center") => {
    switch (align) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      default:
        return "text-left";
    }
  };

  const getAlignStyle = (align = "center") => ({
    textAlign: align,
  });

  const paginationBar = paginate && (
    <div className={`shrink-0 px-3 sm:px-4 ${compact ? "py-2 sm:py-3 border-t border-gray-200 bg-gray-50/50" : "py-2 sm:py-2.5 border-b border-gray-200 bg-white"}`}>
      <div className="flex items-center justify-center sm:justify-between">
        {/* Bilgi - mobilde gizle */}
        <div className={`hidden sm:block ${compact ? "text-sm" : "text-xs"} text-gray-500`}>
          {totalCount} {countText} &bull; Sayfa {safePage}/{totalPages}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <select
            value={pageSize}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10) || defaultPageSize;
              setPageSize(val);
              setCurrentPage(1);
            }}
            className={`hidden sm:block border border-gray-200 rounded-lg ${compact ? "px-2.5 py-1.5 text-sm" : "px-2 py-1 text-xs"} text-gray-600 bg-white cursor-pointer`}
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className={`w-9 h-9 sm:w-auto sm:h-auto flex items-center justify-center ${compact ? "sm:px-3 sm:py-1.5 text-sm" : "sm:px-2.5 sm:py-1 text-sm sm:text-xs"} rounded-lg border font-medium transition-colors ${
                safePage === 1
                  ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              <span className="sm:hidden">‹</span>
              <span className="hidden sm:inline">Önceki</span>
            </button>
            {(() => {
              const buttons = [];
              const maxButtons = 5;
              let startBtn = Math.max(1, safePage - Math.floor(maxButtons / 2));
              let endBtn = Math.min(totalPages, startBtn + maxButtons - 1);
              if (endBtn - startBtn + 1 < maxButtons) {
                startBtn = Math.max(1, endBtn - maxButtons + 1);
              }
              for (let p = startBtn; p <= endBtn; p++) {
                buttons.push(
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`${compact ? "w-9 h-9 sm:w-8 sm:h-8 text-sm" : "w-9 h-9 sm:w-7 sm:h-7 text-sm sm:text-xs"} rounded-lg border font-semibold transition-colors ${
                      p === safePage
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {p}
                  </button>
                );
              }
              return buttons;
            })()}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className={`w-9 h-9 sm:w-auto sm:h-auto flex items-center justify-center ${compact ? "sm:px-3 sm:py-1.5 text-sm" : "sm:px-2.5 sm:py-1 text-sm sm:text-xs"} rounded-lg border font-medium transition-colors ${
                safePage === totalPages
                  ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              <span className="sm:hidden">›</span>
              <span className="hidden sm:inline">Sonraki</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={compact
        ? `flex flex-col h-full overflow-hidden ${className}`
        : // Kart görünümü kaldırıldı — tablo cihaz detayındaki gibi kenardan
          // kenara düz durur (ReportLayout padding'ini negatif margin dengeler).
          // flex-1 + min-h-0: kalan alanı doldurur, scroll tablo İÇİNDE kalır;
          // üstteki filtre/buton alanı sabit durur.
          `bg-white border-y border-gray-200 overflow-hidden -mx-3 sm:-mx-4 flex-1 min-h-0 flex flex-col ${className}`
      }
    >
      {/* Pagination üstte (sadece compact değilse) */}
      {!compact && paginationBar}

      {showHeader && !compact && (
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-xl">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">{title}</h3>
              <p className="text-sm text-gray-600">
                {count} {countText}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Table Content — non-compact'ta da scroll tablo içinde (dikey + yatay) */}
      <div className={compact ? "flex-1 min-h-0 overflow-y-auto" : "flex-1 min-h-0 overflow-auto"}>
        <div className={compact ? "" : "h-full"}>
          {/* Mobilde kolonlar içerik genişliğine göre açılır (auto layout +
              min-w-max) ve kap yatay kaydırılır — içerik/başlık kesilmez.
              sm+ eski davranış: table-fixed, tam genişlik. */}
          <table className={`w-full ${compact ? "table-fixed" : "sm:table-fixed min-w-max sm:min-w-0"}`}>
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    className={`px-4 ${compact ? "py-2 text-[11px]" : "py-3 text-xs"} font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 ${getAlignClass(
                      column.align
                    )} ${column.width || ""} ${column.headerClassName || ""}`}
                    // Sticky + zemin th bazında: thead'e sticky verilince yatay
                    // kaydırmada başlık zemini görünür alan genişliğinde kesiliyordu.
                    style={{
                      ...getAlignStyle(column.align),
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backgroundColor: "#f9fafb",
                    }}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {pagedData.map((item, index) => (
                <tr
                  key={index}
                  className={`hover:bg-indigo-50/40 transition-colors ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                  } ${onRowClick ? "cursor-pointer" : ""}`}
                  onClick={() => onRowClick && onRowClick(item, index)}
                >
                  {columns.map((column, colIndex) => {
                    const allowWrap = column.cellClassName?.includes("whitespace-normal");
                    return (
                      <td
                        key={colIndex}
                        className={`px-4 ${compact ? "py-2.5" : "py-4"} ${allowWrap ? "whitespace-normal align-top" : "whitespace-nowrap"} ${getAlignClass(
                          column.align
                        )} ${column.width || ""} ${column.cellClassName || ""}`}
                        style={getAlignStyle(column.align)}
                      >
                        {renderCell(item, column, index)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination altta (compact modda) */}
      {compact && paginationBar}
    </div>
  );
};

export default ReportTable;
